import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { getTranslator } from '../i18n';

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('fitsocial_user')) || null; } catch { return null; }
  });
  const [lang, setLangState] = useState(() => localStorage.getItem('fitsocial_lang') || 'en');
  const [loading, setLoading] = useState(true);

  const t = getTranslator(lang);

  useEffect(() => {
    document.documentElement.classList.add('dark');
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    const token = localStorage.getItem('fitsocial_token');
    if (!token) {
      localStorage.removeItem('fitsocial_user');
      setUser(null);
      setLoading(false);
      return;
    }
    api.get('/auth/me')
      .then((res) => {
        setUser(res.data);
        localStorage.setItem('fitsocial_user', JSON.stringify(res.data));
      })
      .catch(() => {
        localStorage.removeItem('fitsocial_token');
        localStorage.removeItem('fitsocial_user');
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback((token, userData) => {
    localStorage.setItem('fitsocial_token', token);
    localStorage.setItem('fitsocial_user', JSON.stringify(userData));
    setUser(userData);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('fitsocial_token');
    localStorage.removeItem('fitsocial_user');
    setUser(null);
    window.location.href = '/auth';
  }, []);

  const setLang = useCallback((code) => {
    setLangState(code);
    localStorage.setItem('fitsocial_lang', code);
    if (localStorage.getItem('fitsocial_token')) {
      api.put('/users/me', { language: code }).then((response) => {
        setUser(response.data);
        localStorage.setItem('fitsocial_user', JSON.stringify(response.data));
      }).catch(() => {});
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
      localStorage.setItem('fitsocial_user', JSON.stringify(res.data));
    } catch {
      localStorage.removeItem('fitsocial_token');
      localStorage.removeItem('fitsocial_user');
      setUser(null);
    }
  }, []);

  return (
    <AppContext.Provider value={{ user, login, logout, lang, setLang, t, loading, refreshUser }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
